import { Dropdown, Spin } from 'antd';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';
import { FiCheck } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from '@nxsys/store';
import { AppDispatch } from '@nxsys/store';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';

const SYSTEM_NAMES = {
  payroll: 'Payroll',
  umbrella: 'Umbrella',
} as const;

const SystemSwitcher = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [switching, setSwitching] = useState(false);
  const { isBureau, alternateSystem } = useSelector(
    (state: AppState) => state.appStore,
  );

  const currentName = isBureau ? SYSTEM_NAMES.payroll : SYSTEM_NAMES.umbrella;
  const canSwitch =
    alternateSystem && alternateSystem.isBureau !== isBureau;

  const label = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        marginRight: 16,
        userSelect: 'none',
        cursor: canSwitch ? 'pointer' : 'default',
      }}
    >
      <span style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.2 }}>
        System
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
          {currentName}
        </span>
        {canSwitch && <FaChevronDown size={10} color="#595959" />}
      </div>
    </div>
  );

  if (!canSwitch) return label;

  const alternateName = alternateSystem.isBureau
    ? SYSTEM_NAMES.payroll
    : SYSTEM_NAMES.umbrella;

  const handleSwitch = () => {
    // Show a full-screen overlay first so the current page's re-render with the
    // swapped system data stays hidden until the hard reload boots the new one.
    setSwitching(true);
    dispatch(appActions.switchSystem());
    window.location.href = '/';
  };

  const items = [
    {
      key: 'current',
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minWidth: 160,
            padding: '2px 0',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14 }}>{currentName}</span>
          <FiCheck size={14} color="#52c41a" />
        </div>
      ),
      disabled: true,
    },
    {
      key: 'alternate',
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minWidth: 160,
            padding: '2px 0',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14 }}>{alternateName}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Switch</span>
        </div>
      ),
      onClick: handleSwitch,
    },
  ];

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{ minWidth: 200 }}
      >
        {label}
      </Dropdown>
      {switching &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2147483647,
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
            }}
          >
            <Spin size="large" />
            <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>
              Switching to {alternateName}…
            </span>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SystemSwitcher;
